import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import type { EnumsResponse } from '@/api/enums'
import type { AuthEvent } from '@/types/authEvents'
import { formatDateTimeSecondsDMY } from '@/utils/date'
import {
  AUTH_EVENT_COLOR,
  authEventChipVariant,
  describePriorState,
  describeReason,
  eventTypeLabel,
  shortUserAgent,
} from '@/components/admin/users/authEventsUi'

interface AuthEventDetailsDialogProps {
  event: AuthEvent | null
  open: boolean
  enums: EnumsResponse | null
  onClose: () => void
}

const MONO_SX = { fontFamily: 'monospace', overflowWrap: 'anywhere' } as const

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ mb: 2, minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {children}
    </Box>
  )
}

function Value({ value, mono = false }: { value: string | number | null; mono?: boolean }) {
  return (
    <Typography variant="body1" sx={mono && value !== null ? MONO_SX : { overflowWrap: 'anywhere' }}>
      {value ?? '—'}
    </Typography>
  )
}

/** Все поля строки журнала входов и выходов — для разбора конкретного разлогина */
export function AuthEventDetailsDialog({ event, open, enums, onClose }: AuthEventDetailsDialogProps) {
  const { t } = useTranslation()

  const reason = event ? describeReason(event, enums, t) : null
  const device = event ? shortUserAgent(event.user_agent) : null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('admin.users.authEvents.detailsTitle')}</DialogTitle>
      <DialogContent>
        {event && (
          <Box sx={{ mt: 1 }}>
            <Field label={t('admin.users.authEvents.columnEvent')}>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  size="small"
                  color={AUTH_EVENT_COLOR[event.event]}
                  variant={authEventChipVariant(event.event)}
                  label={eventTypeLabel(enums, event.event)}
                />
              </Box>
            </Field>
            <Field label={t('admin.users.authEvents.columnTime')}>
              <Value value={formatDateTimeSecondsDMY(event.created_at)} />
            </Field>
            <Field label={t('admin.users.authEvents.columnReason')}>
              <Value value={reason} />
              {event.reason && event.reason !== reason && (
                <Typography variant="caption" component="div" color="text.secondary" sx={MONO_SX}>
                  {event.reason}
                </Typography>
              )}
            </Field>
            {event.blacklisted_ago_s !== null && (
              <Field label={t('admin.users.authEvents.fieldBlacklistedAgo')}>
                <Value
                  value={t('admin.users.authEvents.ago', {
                    time: t('admin.users.authEvents.seconds', { count: event.blacklisted_ago_s }),
                  })}
                />
              </Field>
            )}
            {event.prior_state && (
              <Field label={t('admin.users.authEvents.fieldPriorState')}>
                <Value value={describePriorState(event.prior_state, t)} />
                <Typography variant="caption" component="div" color="text.secondary" sx={MONO_SX}>
                  {event.prior_state}
                </Typography>
              </Field>
            )}
            <Field label={t('admin.users.authEvents.fieldSessionStart')}>
              <Value value={event.session_iat ? formatDateTimeSecondsDMY(event.session_iat) : null} />
            </Field>
            <Field label={t('admin.users.authEvents.fieldToken')}>
              <Value value={event.jti_hash} mono />
            </Field>
            <Field label={t('admin.users.authEvents.fieldParentToken')}>
              <Value value={event.parent_jti_hash} mono />
            </Field>
            <Field label={t('admin.users.authEvents.fieldClient')}>
              <Value value={event.client} />
            </Field>
            <Field label={t('admin.users.authEvents.fieldClientVersion')}>
              <Value value={event.client_version} mono />
            </Field>
            <Field label={t('admin.users.authEvents.fieldTab')}>
              <Value value={event.tab_id} mono />
            </Field>
            <Field label={t('admin.users.authEvents.fieldIp')}>
              <Value value={event.ip} mono />
            </Field>
            <Field label={t('admin.users.authEvents.fieldUserAgent')}>
              {device && <Value value={device} />}
              <Typography variant="caption" component="div" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                {event.user_agent ?? '—'}
              </Typography>
            </Field>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}
